import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  CalendarDays,
  User,
  Clock,
  Tag,
  ArrowLeft,
  Bookmark,
  Share2,
  Facebook,
  Twitter,
  Linkedin,
  Mail,
  ChevronRight,
} from "lucide-react";

import Footer from "../layout/Footer";
import Header from "../layout/Header";
import { blogPosts, relatedPosts } from "../../utils/blogData";
import "../../style/blogContent.css";

const BlogDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isBookmarked, setIsBookmarked] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const post = blogPosts.find((p) => p.id === parseInt(id));

  if (!post) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <Header />
        <div className="container mx-auto px-4 py-24 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center">
              <div className="w-12 h-12 bg-gray-400 rounded-full"></div>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-3">
              Post Not Found
            </h1>
            <p className="text-gray-600 mb-8">
              The blog post you're looking for doesn't exist or has been moved.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all duration-300 group"
            >
              <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
              Back to Blog
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = encodeURIComponent(post.title);
    const text = encodeURIComponent(post.excerpt);

    const platforms = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?text=${title}&url=${url}`,
      linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${title}`,
      mail: `mailto:?subject=${title}&body=${text}%0A%0A${url}`,
    };

    if (platforms[platform]) {
      window.open(platforms[platform]);
    }
  };

  const ShareButton = ({ platform, icon: Icon, color }) => (
    <button
      onClick={() => handleShare(platform)}
      className={`p-3 rounded-full ${color} hover:scale-110 transition-transform duration-200 hover:shadow-lg`}
      aria-label={`Share on ${platform}`}
      type="button"
    >
      <Icon className="w-5 h-5 text-white" />
    </button>
  );

  return (
    <div className="min-h-screen  ">
      <Header />

      {/* Sticky top bar (clean like screenshot) */}
      <div className="sticky top-0 z-50 bg-white/90 backdrop-blur-lg border-b border-gray-100">
        <div className="px-4 sm:px-6 lg:px-8 ">
          <div className="flex items-center justify-between py-3">
            <button
              onClick={() => navigate("/blog")}
              className="flex items-center text-indigo-600 hover:text-indigo-700 transition-colors group"
              type="button"
            >
              <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
              <span className="font-semibold">Back to Blog</span>
            </button>


          </div>
        </div>
      </div>

      {/* Main content (editorial layout) */}
      <main className="max-w-7xl mx-auto pt-4 max-md:px-4">
        {/* Editorial Hero */}
        <section className="blog-hero">
          <div className="blog-hero-meta">
            <div className="blog-meta-row">
              <span className="blog-pill ">{post.category}</span>
              <span className="blog-meta-item">
                <CalendarDays className="w-4 h-4" />
                {post.date}
              </span>
              <span className="blog-meta-item">
                <Clock className="w-4 h-4" />
                {post.readTime} read
              </span>
              <span className="blog-meta-item">
                <User className="w-4 h-4" />
                {post.author}
              </span>
            </div>
          </div>

          <h1 className="blog-title">{post.title}</h1>
          <p className="blog-subtitle">{post.excerpt}</p>

          <div className="blog-hero-image-wrap">
            <img
              src={post.image}
              alt={post.title}
              className="blog-hero-image"
              loading="lazy"
            />
          </div>

          {/* Tags row under hero (optional, looks nice) */}
          <div className="blog-tags">
            <Tag className="w-5 h-5 text-slate-400" />
            {post.tags.map((tag, i) => (
              <span key={i} className="blog-tag">
                {tag}
              </span>
            ))}
          </div>
        </section>

        {/* Article body */}
        <article className="blog-article">
          <div className="blog-article-inner">
            <div
              className="blog-content blog-numbered"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </div>

          {/* Share Section */}
          {/* <div className="blog-share">
            <div className="blog-share-left">
              <h3>Enjoyed this article?</h3>
              <p>Share it with your network</p>
            </div>
            <div className="flex items-center gap-3">
              <ShareButton
                platform="facebook"
                icon={Facebook}
                color="bg-blue-600 hover:bg-blue-700"
              />
              <ShareButton
                platform="twitter"
                icon={Twitter}
                color="bg-sky-500 hover:bg-sky-600"
              />
              <ShareButton
                platform="linkedin"
                icon={Linkedin}
                color="bg-blue-700 hover:bg-blue-800"
              />
              <ShareButton
                platform="mail"
                icon={Mail}
                color="bg-gray-600 hover:bg-gray-700"
              />
            </div>
          </div> */}

          {/* Author Bio */}
        </article>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="blog-related">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Related Articles
                </h2>
                <p className="text-gray-600 mt-2">
                  Continue reading with these handpicked stories
                </p>
              </div>
              <Link
                to="/blog"
                className="hidden sm:flex items-center text-indigo-600 hover:text-indigo-700 font-medium"
              >
                View all
                <ChevronRight className="w-5 h-5 ml-1" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-10">
              {relatedPosts.map((rp) =>
                rp.id != id ? (
                  <Link
                    key={rp.id}
                    to={`/blog/${rp.id}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    className="group bg-white rounded-md shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden hover:-translate-y-1"
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={rp.image}
                        alt={rp.title}
                        className="h-48 w-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-white/90 backdrop-blur text-sm font-medium rounded-full">
                          {rp.category}
                        </span>
                      </div>
                    </div>

                    <div className="p-5">
                      <h3 className="font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-indigo-600 transition-colors">
                        {rp.title}
                      </h3>
                      <p className="text-sm text-gray-600 line-clamp-2 mb-4">
                        {rp.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">{rp.date}</span>
                        <span className="flex items-center text-sm text-indigo-600 font-medium">
                          Read more
                          <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </div>
                  </Link>
                ) : null
              )}

            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default BlogDetailPage;
